import type {
  MissionEntityRef,
  TaskAgentRef,
  TaskEntityRef,
} from "@/services/api/agentic-v2";

/** Keep the previous text when a WS patch sends null or an empty string. */
export function mergeNullableTextField(
  previous: string | null | undefined,
  incoming: string | null | undefined,
): string | null {
  if (incoming == null) return previous ?? null;
  const trimmed = incoming.trim();
  if (trimmed) return incoming;
  return previous ?? null;
}

/** True when the API entity type refers to a household / fa_client row. */
export function isClientEntityType(entityType: string | null | undefined): boolean {
  return entityType === "fa_client" || entityType === "client";
}

/** Best-effort label for chips, subtitles, and window titles. */
export function entityRefLabel(entity: MissionEntityRef | TaskEntityRef | null | undefined): string {
  if (!entity) return "";
  const name = entity.display_name?.trim();
  if (name) return name;
  return entity.entity_id ?? "";
}

/** Preserve hydrated display names when a WS patch omits them. */
export function mergeMissionEntityRefs(
  previous: MissionEntityRef[],
  incoming: MissionEntityRef[],
): MissionEntityRef[] {
  if (incoming.length === 0) return previous;
  return incoming.map((entity) => {
    const match = previous.find(
      (row) =>
        row.entity_type === entity.entity_type && row.entity_id === entity.entity_id,
    );
    if (!entity.display_name?.trim() && match?.display_name?.trim()) {
      return {
        ...entity,
        display_name: match.display_name,
        lead_detail: entity.lead_detail?.trim() ? entity.lead_detail : match.lead_detail,
      };
    }
    if (!entity.lead_detail?.trim() && match?.lead_detail?.trim()) {
      return { ...entity, lead_detail: match.lead_detail };
    }
    return entity;
  });
}

/** Preserve hydrated agent names when patching task list/detail caches. */
export function mergeTaskAgentRef(
  previous: TaskAgentRef | null | undefined,
  incoming: TaskAgentRef | null | undefined,
): TaskAgentRef | null {
  if (incoming == null) return previous ?? null;
  if (!incoming.id) return previous ?? null;
  const incomingName = incoming.name?.trim();
  if (!previous) {
    return incomingName ? incoming : { ...incoming, name: "Agent" };
  }
  return {
    ...previous,
    ...incoming,
    name: incomingName || previous.name,
    role: incoming.role ?? previous.role,
    avatar_colour: incoming.avatar_colour ?? previous.avatar_colour,
  };
}

/** Preserve hydrated task entity names when patching list/detail caches. */
export function mergeTaskEntityRef(
  previous: TaskEntityRef | null | undefined,
  incoming: TaskEntityRef | null | undefined,
): TaskEntityRef | null {
  if (incoming == null) return previous ?? null;
  if (!previous) return incoming;
  return {
    ...previous,
    ...incoming,
    display_name: incoming.display_name?.trim()
      ? incoming.display_name
      : previous.display_name,
    lead_detail: incoming.lead_detail?.trim()
      ? incoming.lead_detail
      : previous.lead_detail,
    lead_status: incoming.lead_status?.trim()
      ? incoming.lead_status
      : previous.lead_status,
  };
}

/** Entity refs that still need a fa_client name lookup. */
export function missionEntitiesMissingDisplayName(
  entities: MissionEntityRef[],
): string[] {
  const ids: string[] = [];
  for (const entity of entities) {
    if (!entity.entity_id) continue;
    if (!isClientEntityType(entity.entity_type)) continue;
    if (entity.display_name?.trim()) continue;
    ids.push(entity.entity_id);
  }
  return ids;
}
